<script lang="ts">
import type { DropdownMenuContentEmits, DropdownMenuContentProps, DropdownMenuRootEmits, DropdownMenuRootProps } from 'reka-ui'
import type { ArrayOrNested, EmitsToProps } from '../types/utils'

export interface DropdownMenuItem {
  label?: string
  helperText?: string
  icon?: string
  color?: 'primary' | 'critical'
  content?: Omit<DropdownMenuContentProps, 'as' | 'asChild' | 'forceMount'> & Partial<EmitsToProps<DropdownMenuContentEmits>>
  type?: 'label' | 'separator' | 'link'
  loading?: boolean
  disabled?: boolean
  open?: boolean
  defaultOpen?: boolean
  children?: ArrayOrNested<DropdownMenuItem>
  onClick?: (e: Event) => void
  class?: string | object
  textClass?: string | object
  [key: string]: any
}

export interface DropdownMenuProps<T extends ArrayOrNested<DropdownMenuItem> = ArrayOrNested<DropdownMenuItem>> extends Omit<DropdownMenuRootProps, 'dir'> {
  items?: T
  content?: Omit<DropdownMenuContentProps, 'as' | 'asChild' | 'forceMount'> & Partial<EmitsToProps<DropdownMenuContentEmits>>
  portal?: boolean | string | HTMLElement
  disabled?: boolean
  class?: string | object
}

export interface DropdownMenuEmits extends DropdownMenuRootEmits {}

export interface DropdownMenuSlots {
  default: (props: { open: boolean }) => any
  demo?: () => any
}

const ui = {
  content: 'min-w-32 bg-white shadow-lg border border-secondary-400 rounded-lg overflow-hidden flex flex-col',
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<DropdownMenuItem>">
import { reactivePick } from '@vueuse/core'
import { defu } from 'defu'
import { DropdownMenuRoot, DropdownMenuTrigger, useForwardPropsEmits } from 'reka-ui'
import { computed, toRef } from 'vue'
import AwDropdownMenuContent from './DropdownMenuContent.vue'

const props = withDefaults(defineProps<DropdownMenuProps<T>>(), {
  portal: true,
  modal: true,
  externalIcon: true,
})
const emits = defineEmits<DropdownMenuEmits>()
const slots = defineSlots<DropdownMenuSlots>()

const rootProps = useForwardPropsEmits(reactivePick(props, 'defaultOpen', 'open', 'modal'), emits)
const contentProps = toRef(() => defu(props.content, { side: 'bottom', align: 'start', sideOffset: 8, collisionPadding: 8 }) as DropdownMenuContentProps)

const contentClass = computed(() => {
  return [
    ui.content,
    props.class,
  ]
})
</script>

<template>
  <DropdownMenuRoot v-slot="{ open }" v-bind="rootProps">
    <DropdownMenuTrigger v-if="!!slots.default" as-child :class="props.class" :disabled>
      <slot :open />
    </DropdownMenuTrigger>

    <AwDropdownMenuContent
      v-if="open"
      :class="contentClass"
      v-bind="contentProps"
      :items
      :portal
    >
      <slot name="demo" />
    </AwDropdownMenuContent>
  </DropdownMenuRoot>
</template>
